import { useMemo, useState } from 'react';

import { Button } from '../ui/Button.jsx';
import { Modal } from '../ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { parseCsv } from '../../utils/csv.js';

/**
 * Generic CSV import modal.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.title
 * @param {Array<{ field: string, label: string, aliases?: string[], required?: boolean }>} props.fields
 *        Target fields to map CSV columns onto. `aliases` lists accepted header names.
 * @param {(row: object) => Promise<any>} props.onImportRow  - creates one record; may throw.
 * @param {() => void} [props.onDone]  - called after a run completes (e.g. to refetch).
 */
export function CsvImportModal({ open, onClose, title, fields, onImportRow, onDone }) {
  const { t } = useTranslation();
  const [parsed, setParsed] = useState(null); // { headers, rows }
  const [parseError, setParseError] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // { ok, failed: [{ line, message }] }

  function reset() {
    setParsed(null);
    setParseError(null);
    setRunning(false);
    setResult(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleFile(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setParseError(null);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const out = parseCsv(String(reader.result));
        if (out.rows.length === 0) {
          setParseError(t('csv.import.empty'));
          setParsed(null);
          return;
        }
        setParsed(out);
      } catch {
        setParseError(t('csv.import.parseError'));
        setParsed(null);
      }
    };
    reader.readAsText(file, 'utf-8');
  }

  // Map a CSV row (keyed by header) onto the target fields using label/field/aliases.
  const mapRow = useMemo(() => {
    return (csvRow) => {
      const lowerEntries = Object.entries(csvRow).map(([k, v]) => [k.trim().toLowerCase(), v]);
      const lookup = new Map(lowerEntries);
      const out = {};
      for (const f of fields) {
        const names = [f.field, f.label, ...(f.aliases || [])].map((n) => String(n).toLowerCase());
        let value = '';
        for (const n of names) {
          if (lookup.has(n) && lookup.get(n) !== '') {
            value = lookup.get(n);
            break;
          }
        }
        out[f.field] = value;
      }
      return out;
    };
  }, [fields]);

  const previewRows = parsed ? parsed.rows.slice(0, 5).map(mapRow) : [];
  const missingRequired = useMemo(() => {
    if (!parsed) {
      return [];
    }
    const mappedFirst = mapRow(parsed.rows[0]);
    return fields.filter((f) => f.required && !mappedFirst[f.field]);
  }, [parsed, fields, mapRow]);

  async function runImport() {
    if (!parsed) {
      return;
    }
    setRunning(true);
    const failed = [];
    let ok = 0;
    for (let i = 0; i < parsed.rows.length; i += 1) {
      const mapped = mapRow(parsed.rows[i]);
      try {
        // eslint-disable-next-line no-await-in-loop
        await onImportRow(mapped);
        ok += 1;
      } catch (err) {
        failed.push({ line: i + 2, message: err?.message || 'Error' }); // +2: header + 1-based
      }
    }
    setRunning(false);
    setResult({ ok, failed });
    if (ok > 0 && onDone) {
      onDone();
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>{t('common.cancel')}</Button>
          {parsed && !result && (
            <Button
              variant="primary"
              onClick={runImport}
              disabled={running || missingRequired.length > 0}
            >
              {running
                ? `${t('csv.import.running')}…`
                : `${t('csv.import.start')} (${parsed.rows.length})`}
            </Button>
          )}
        </>
      }
    >
      <div className="csv-import">
        <p className="muted text-sm">{t('csv.import.intro')}</p>
        <div className="csv-import-fields">
          <span className="field-label">{t('csv.import.expectedColumns')}</span>
          <div className="csv-chip-row">
            {fields.map((f) => (
              <span key={f.field} className={`csv-chip${f.required ? ' required' : ''}`}>
                {f.label}{f.required ? ' *' : ''}
              </span>
            ))}
          </div>
        </div>

        <label className="field">
          <span className="field-label">{t('csv.import.file')}</span>
          <input type="file" accept=".csv,text/csv" className="input" onChange={handleFile} />
        </label>

        {parseError && <div className="alert danger">{parseError}</div>}

        {missingRequired.length > 0 && (
          <div className="alert danger">
            {t('csv.import.missingColumns')}: {missingRequired.map((f) => f.label).join(', ')}
          </div>
        )}

        {parsed && !result && (
          <div className="csv-preview">
            <span className="field-label">
              {t('csv.import.preview')} ({parsed.rows.length} {t('csv.import.rows')})
            </span>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>{fields.map((f) => <th key={f.field}>{f.label}</th>)}</tr>
                </thead>
                <tbody>
                  {previewRows.map((row, idx) => (
                    <tr key={idx}>
                      {fields.map((f) => <td key={f.field}>{row[f.field]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {result && (
          <div className={result.failed.length === 0 ? 'alert success' : 'alert danger'}>
            <strong>{t('csv.import.done')}:</strong> {result.ok} {t('csv.import.imported')}
            {result.failed.length > 0 && (
              <>
                {', '}{result.failed.length} {t('csv.import.failedCount')}.
                <ul className="csv-error-list">
                  {result.failed.slice(0, 8).map((f) => (
                    <li key={f.line}>{t('csv.import.line')} {f.line}: {f.message}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
